import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useMemo } from "react";

import useTheme from "@mui/material/styles/useTheme";

import CircularProgress from "components/circular-progress";
import Dialog from "components/dialog";
import { TEAMS_FEATURE, TEAM_MENTIONS_FEATURE } from "constants/feature-flags";
import useStaffTasksV2 from "features/staff-tasks-v2/hooks/use-staff-tasks-v2.hook";
import { useGetStaffTaskQuery } from "services/staff-tasks";

import EditStaffTaskForm from "./edit-staff-task-form";
import TaskNotFound from "./task-not-found.component";
import ViewStaffTaskForm from "./view-staff-task-form";

export default function StaffTaskModalDialog() {
  const theme = useTheme();
  // The task's @mentions are read only with the flag on: an older backend 400s the RestQL keys.
  const flags = useFlags();
  const includeMentions = Boolean(flags[TEAM_MENTIONS_FEATURE]);
  // Likewise the team the task was sent to (TEAMS_FEATURE).
  const includeTeam = Boolean(flags[TEAMS_FEATURE]);
  const {
    taskModalMode,
    isUpdating,
    currentTaskId,
    handleCloseTaskModal,
    handleOpenTaskModal,
    clearTaskListData
  } = useStaffTasksV2();

  const isNew = taskModalMode.displayMode === "create";
  const isEdit = taskModalMode.displayMode === "edit";
  const isView = taskModalMode.displayMode === "view";

  const {
    data: task,
    isFetching,
    isLoading,
    isUninitialized,
    error
  } = useGetStaffTaskQuery(
    // Flag off: the same number arg as before mentions, so the cache entry is shared with every other
    // caller (patient-task-form passes the bare id) and nothing is fetched twice.
    includeMentions || includeTeam
      ? { id: Number(currentTaskId), includeMentions, includeTeam }
      : Number(currentTaskId),
    { skip: !currentTaskId || isNew }
  );

  const isBusy = useMemo(
    () => [isFetching, isLoading, isUninitialized, isUpdating].some(Boolean),
    [isFetching, isLoading, isUninitialized, isUpdating]
  );

  const taskDetails = useMemo(() => {
    if (error && "status" in error && error.status === 404) {
      return <TaskNotFound />;
    }

    if (isEdit || isNew) {
      return (
        <EditStaffTaskForm
          task={isEdit && !isBusy ? task : undefined}
          onSaveCallback={clearTaskListData}
        />
      );
    }

    if (currentTaskId === task?.id && isView) {
      return <ViewStaffTaskForm task={task} isBusy={isBusy} />;
    }

    return <CircularProgress sx={{ width: "100%" }} />;
  }, [
    clearTaskListData,
    currentTaskId,
    error,
    isBusy,
    isEdit,
    isNew,
    isView,
    task
  ]);

  return (
    <Dialog
      title={isEdit ? "Edit Task" : isNew ? "New Task" : "View Task"}
      onClose={(event: any, reason: string) => {
        // A click outside a half-filled form is usually accidental, so it is
        // ignored while editing. Escape is deliberate and does exactly what the
        // Cancel button does (no confirm there either), so it goes through.
        if ((isEdit || isNew) && reason === "backdropClick") {
          return;
        }
        if (
          currentTaskId &&
          taskModalMode.onEditCancelMode === "openViewModal"
        ) {
          handleOpenTaskModal({ mode: "view", taskId: currentTaskId });
        } else {
          handleCloseTaskModal();
        }
      }}
      open={Boolean(isNew || isEdit || isView)}
      minWidth={isView ? 1500 : 1200}
      maxWidth={isView ? "xl" : "lg"}
      sx={{
        zIndex: theme.zIndex.snackbar + 2,
        ...(isView && {
          "& .MuiDialog-paper": {
            width: "calc(100vw - 240px)",
            minWidth: 0,
            maxWidth: "1500px"
          }
        })
      }}
      dialogTitleSx={{ pl: "30px" }}
      dialogContentProps={{
        sx: {
          boxSizing: "border-box",
          padding: "40px 40px !important",
          minWidth: isView ? 0 : "1200px",
          maxWidth: isView ? "100%" : "1200px",
          minHeight: "665px",
          maxHeight: "900px"
        }
      }}
    >
      {taskDetails}
    </Dialog>
  );
}
